import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import type { AuthRecord, Permissions } from 'src/context/authContext'
import {
  AccessManagementAbsoluteRoutes,
  AiGrcQaAbsoluteRoutes,
  AuditBotAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  DirectRoutes,
  EvidenceAbsoluteRoutes,
  ExamineAndTestAbsoluteRoutes,
  FrameworksAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ReceptorsAbsoluteRoutes,
  RequestsAbsoluteRoutes,
  RisksAbsoluteRoutes,
  RoadmapAbsoluteRoutes,
  SchedulingAbsoluteRoutes,
  ScopeAbsoluteRoutes,
  SecurityQuestionnaireAbsoluteRoutes,
  ServicesAbsoluteRoutes,
  TrustPortalConfigurationAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  UserRolesAbsoluteRoutes,
  VendorsAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
// Page ownership is explicit. A more specific destination (including a tab URL)
// owns its access decision independently of its parent route.
export const PAGE_ACCESS_CAPABILITY_ROUTES: Partial<
  Record<CAPABILITY, string[]>
> = {
  [CAPABILITY.HAS_PAGE_ACCESS_CONTROLS]: [ControlsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_POLICIES]: [PoliciesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_EVIDENCE]: [EvidenceAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RISKS]: [RisksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_SERVICES]: [ServicesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RECEPTORS]: [
    ReceptorsAbsoluteRoutes.ROOT,
    DirectRoutes.DOCUMENT_MANAGEMENT,
    DirectRoutes.WORKFLOW_MANAGEMENT,
    DirectRoutes.GRC_PLATFORMS,
    DirectRoutes.INTEGRATIONS_LIST,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_FRAMEWORKS]: [FrameworksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDITS]: [DirectRoutes.AUDITS],
  [CAPABILITY.HAS_PAGE_ACCESS_REQUESTS]: [RequestsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_REPORTS]: [DirectRoutes.REPORTS],
  [CAPABILITY.HAS_PAGE_ACCESS_ROADMAP]: [RoadmapAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS]: [
    DirectRoutes.DASHBOARD,
    DirectRoutes.GRC_DASHBOARDS,
    DirectRoutes.BUYER_DASHBOARD,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_USERS]: [DirectRoutes.USERS],
  [CAPABILITY.HAS_PAGE_ACCESS_SCOPE]: [ScopeAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDIT_SCANS]: [
    ExamineAndTestAbsoluteRoutes.ROOT,
    AuditBotAbsoluteRoutes.ROOT,
    ExamineAndTestAbsoluteRoutes.COMPLIANCE_ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUST_PORTAL]: [
    TrustPortalConfigurationAbsoluteRoutes.ROOT,
    AccessManagementAbsoluteRoutes.ROOT,
  ],
  // security_questionnaire is the deprecated route the live ai_grc_qa page replaced;
  // both still resolve, so both have to be denied together.
  [CAPABILITY.HAS_PAGE_ACCESS_QUESTIONNAIRES]: [
    SecurityQuestionnaireAbsoluteRoutes.ROOT,
    AiGrcQaAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_SETTINGS]: [
    DirectRoutes.ACCOUNT_SETTINGS,
    DirectRoutes.API_TOKENS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_VENDORS]: [VendorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_COMPANY_INFO]: [DirectRoutes.COMPANY_INFO],
  [CAPABILITY.HAS_PAGE_ACCESS_SCHEDULES]: [SchedulingAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUSTERO_INTELLIGENCE]: [
    TrusteroIntelligenceAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_DETAILS]: [
    DirectRoutes.ACTIVITIES,
    DirectRoutes.USAGE,
    DirectRoutes.INDEXING,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCESS_ROLES]: [
    UserRolesAbsoluteRoutes.INDEX,
    'users?tab=user-group',
    UserRolesAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_NOTIFICATIONS]: [
    'your_preference?tab=notifications',
    'settings/notifications',
    'account_settings?tab=notifications',
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_GRC_DOCUMENTS]: [DirectRoutes.MANAGE_DOCUMENTS],
}

export const canAccessPageCapability = (
  permissions: Permissions | undefined,
  email: string | undefined,
  capability: CAPABILITY,
): boolean => {
  const rule = permissions?.capabilities[capability]?.rule
  return (
    rule === PERMISSION_RULE.ALWAYS ||
    (rule === PERMISSION_RULE.IF_TRUSTERO_USER &&
      !!email?.toLowerCase().endsWith('@trustero.com'))
  )
}

const entries = Object.entries(PAGE_ACCESS_CAPABILITY_ROUTES)
  .flatMap(([capability, routes]) =>
    (routes || []).map((route) => ({
      route,
      capability: Number(capability) as CAPABILITY,
    })),
  )
  .sort((a, b) => b.route.length - a.route.length)

// Full URLs include an account[:audit] prefix; nav routes are account-relative.
export const pageRoute = (destination: string): string =>
  destination.startsWith('/')
    ? destination.split('/').slice(2).join('/')
    : destination

export const getPageAccessRule = (
  destination: string,
): { route: string; capability: CAPABILITY } | undefined => {
  const relative = pageRoute(destination)
  const [encodedPath, query = ''] = relative.split('#')[0].split('?')
  let path = encodedPath
  try {
    path = decodeURIComponent(encodedPath)
  } catch {
    /* The router handles malformed paths. */
  }
  const params = new URLSearchParams(query)
  return entries.find(({ route }) => {
    const [base, requiredQuery] = route.split('?')
    if (path !== base && !path.startsWith(`${base}/`)) return false
    return (
      !requiredQuery ||
      Array.from(new URLSearchParams(requiredQuery)).every(
        ([key, value]) => params.get(key) === value,
      )
    )
  })
}

export const canAccessPage = (
  auth: Pick<AuthRecord, 'permissions' | 'email'>,
  destination: string,
): boolean => {
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(destination)) {
    try {
      const url = new URL(destination, window.location.href)
      if (url.origin !== window.location.origin) return true
      destination = url.pathname + url.search
    } catch {
      return true
    }
  }
  const rule = getPageAccessRule(destination)
  return (
    !rule ||
    canAccessPageCapability(auth.permissions, auth.email, rule.capability)
  )
}

export const isDeniedPageRoute = (
  denied: string[],
  destination: string,
): boolean => {
  const match = getPageAccessRule(destination)
  if (match) return denied.includes(match.route)
  const route = pageRoute(destination).split(/[?#]/)[0]
  return denied.some(
    (entry) => route === entry || route.startsWith(`${entry}/`),
  )
}
